import React from 'react';
import { Upload, ClipboardPaste, LayoutTemplate, Sparkles, Info } from 'lucide-react';
import { parseResumeText } from '../utils.js';
import BackgroundLayout from './ui/BackgroundLayout';
import { useTheme } from '../contexts/ThemeContext';

const Onboarding = ({ setStep, setResumeData, showNotification, onStartFresh }) => {
    const { isDarkMode } = useTheme();

    const handleFileUpload = (e) => {
        const file = e.target.files[0];
        if (!file) return;

        // If it's a JSON file, parse it directly
        if (file.type === "application/json" || file.name.endsWith(".json")) {
            const reader = new FileReader();
            reader.onload = (event) => {
                try {
                    const parsed = JSON.parse(event.target.result);
                    setResumeData(parsed);
                    setStep('editor');
                    showNotification("Resume loaded successfully!");
                } catch (e) {
                    showNotification("Error parsing JSON file.");
                }
            };
            reader.readAsText(file);
        }
        // If it's a text file, we can read it directly
        else if (file.type === "text/plain" || file.name.endsWith(".txt")) {
            const reader = new FileReader();
            reader.onload = (event) => {
                const parsed = parseResumeText(event.target.result);
                setResumeData(parsed);
                setStep('editor');
                showNotification("Text resume parsed!");
            };
            reader.readAsText(file);
        } else {
            showNotification("Unsupported file. Upload .json or .txt");
        }
    };

    return (
        <BackgroundLayout className="flex items-center justify-center p-6">
            <div className={`max-w-2xl w-full rounded-2xl shadow-2xl overflow-hidden border relative group transition-colors duration-300 ${isDarkMode
                ? 'glass-dark border-white/10'
                : 'bg-white/80 border-white/50 shadow-blue-500/5'
                }`}
            >
                {/* Decorative Glow */}
                <div className={`absolute inset-0 transition-colors pointer-events-none ${isDarkMode ? 'bg-blue-500/5 group-hover:bg-blue-500/10' : 'bg-gradient-to-br from-blue-50/50 to-purple-50/50'}`}></div>

                <div className="p-8 pb-4 text-center relative z-10">
                    <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-gradient-to-br from-blue-600 to-purple-600 mb-6 shadow-lg shadow-blue-500/30">
                        <Sparkles className="text-white" size={32} />
                    </div>
                    <h1 className={`text-4xl font-extrabold mb-2 tracking-tight ${isDarkMode ? 'text-white' : 'text-slate-800'}`}>Resume Architect</h1>
                    <p className={`text-lg ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>Choose how you want to begin your journey.</p>
                </div>

                <div className="p-8 space-y-6 relative z-10">

                    {/* File Upload (Text files mainly) */}
                    <div className={`border-2 border-dashed rounded-xl p-8 text-center transition-all cursor-pointer group/upload relative ${isDarkMode
                        ? 'border-slate-700/50 hover:border-blue-500/50 hover:bg-slate-800/50'
                        : 'border-slate-300 hover:border-blue-500 hover:bg-blue-50'
                        }`}>
                        <input
                            type="file"
                            accept=".txt,.json"
                            onChange={handleFileUpload}
                            className="absolute inset-0 w-full h-full opacity-0 cursor-pointer z-20"
                        />
                        <div className={`w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4 group-hover/upload:scale-110 transition-transform ${isDarkMode
                            ? 'bg-slate-800 group-hover/upload:bg-blue-500/20'
                            : 'bg-blue-100'
                            }`}>
                            <Upload className={isDarkMode ? 'text-blue-400' : 'text-blue-600'} size={28} />
                        </div>
                        <h3 className={`text-lg font-bold mb-1 ${isDarkMode ? 'text-white' : 'text-slate-800'}`}>Upload Resume</h3>
                        <p className={`text-sm ${isDarkMode ? 'text-slate-500' : 'text-slate-500'}`}>Upload .json (saved) or .txt (to parse)</p>
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                        {/* Paste Option */}
                        <button
                            onClick={() => setStep('paste-mode')}
                            className={`flex flex-col items-center justify-center p-6 rounded-xl transition-colors border border-transparent group/btn ${isDarkMode
                                ? 'bg-slate-800/50 hover:bg-slate-800 hover:border-slate-700'
                                : 'bg-slate-50 hover:bg-slate-100 hover:border-slate-200'
                                }`}
                        >
                            <ClipboardPaste className={`mb-2 group-hover/btn:scale-110 transition-transform ${isDarkMode ? 'text-purple-400' : 'text-purple-600'}`} size={24} />
                            <span className={`font-semibold ${isDarkMode ? 'text-white' : 'text-slate-700'}`}>Paste Text</span>
                            <span className="text-xs text-slate-500 mt-1">From PDF or LinkedIn</span>
                        </button>

                        {/* Start Fresh */}
                        <button
                            onClick={onStartFresh}
                            className={`flex flex-col items-center justify-center p-6 rounded-xl transition-colors border border-transparent group/btn ${isDarkMode
                                ? 'bg-slate-800/50 hover:bg-slate-800 hover:border-slate-700'
                                : 'bg-slate-50 hover:bg-slate-100 hover:border-slate-200'
                                }`}
                        >
                            <LayoutTemplate className={`mb-2 group-hover/btn:scale-110 transition-transform ${isDarkMode ? 'text-emerald-400' : 'text-emerald-600'}`} size={24} />
                            <span className={`font-semibold ${isDarkMode ? 'text-white' : 'text-slate-700'}`}>Start Fresh</span>
                            <span className="text-xs text-slate-500 mt-1">No resume? Build from scratch</span>
                        </button>
                    </div>

                    {/* Cold-start disclosure — AI starts dumb, gets smarter as it learns you */}
                    <div className={`rounded-xl p-4 border ${isDarkMode
                        ? 'bg-blue-500/10 border-blue-500/20'
                        : 'bg-blue-50 border-blue-100'
                        }`}>
                        <div className="flex items-start gap-3">
                            <Info size={16} className={`shrink-0 mt-0.5 ${isDarkMode ? 'text-blue-300' : 'text-blue-600'}`} />
                            <div className="text-xs leading-relaxed">
                                <p className={`font-bold mb-1 ${isDarkMode ? 'text-blue-200' : 'text-blue-900'}`}>
                                    The AI is honest, not psychic.
                                </p>
                                <p className={isDarkMode ? 'text-slate-400' : 'text-slate-600'}>
                                    First outputs may feel generic — by design. The more you put in (real experience, real metrics, your writing voice in the summary), the sharper the AI's suggestions become. We never invent skills, companies, or numbers you didn't enter.
                                </p>
                            </div>
                        </div>
                    </div>

                </div>
            </div>
        </BackgroundLayout>
    );
};

export default Onboarding;
